import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRocket,
  faUsers,
  faArrowRight,
  faPlay,
  faShieldAlt,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import ServicesSection from "./Services";
import OperationModelSection from "./OperationModel";
import WhyChooseUsSection from "./WhyChooseUs";
import AboutUs from "./AboutUs";
import ContactUs from "./ContactUs";
import CEOQuotes from "./CEOQuotes";
import { baseUrl } from "../../config";
import { trackEvent } from "../../components/trackingUtils";
import SEOHead from "../../common/SEOHead";
import { getHomepageSchema } from "../../utils/seoUtils";

import "./Homepage.css";

const Homepage = ({ services, blogs }) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const quotes = [
    {
      text: "Focus on building products that put the user first — clarity in purpose drives better engineering decisions.",
      author: "Sundar Pichai — Google",
    },
    {
      text: "Sustainable growth comes from solving real problems with frugal, reliable engineering and long-term focus.",
      author: "Sridhar Vembu — Zoho",
    },
    {
      text: "A company succeeds when it fosters learning, discipline, and a strong sense of purpose across the team.",
      author: "N. R. Narayana Murthy — Infosys",
    },
    // Add more quotes as needed
  ];

  useEffect(() => {
    const trackPageLoad = async () => {
      try {
        if (window.location.pathname === "/") {
          await trackEvent("PageLoad", "", "Homepage");
        }
      } catch (error) {
        console.error("Error tracking page load:", error);
      } finally {
        setIsLoading(false);
      }
    };

    trackPageLoad();
  }, []);

  // Scroll progress indicator
  useEffect(() => {
    const updateScrollProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(scrollTop / docHeight, 1) * 100;
      setScrollProgress(progress);
    };

    const handleScroll = () => {
      updateScrollProgress();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0b1220] via-[#142032] to-[#0b1220]">
        <div className="text-center space-y-4">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-[#E5C558] mb-6"></div>
          <p className="text-xl text-gray-300 font-medium">
            Loading amazing content...
          </p>
        </div>
      </div>
    );
  }

  const title =
    "GeniusNexa - Leading Technology Partner | AI, Cloud & Digital Transformation Solutions";
  const description =
    "Transform your business with GeniusNexa's cutting-edge technology solutions. Expert AI development, cloud computing, custom software, and digital transformation services. Trusted by startups and enterprises. Get a free consultation today!";
  const keywords =
    "GeniusNexa, technology solutions, AI development, artificial intelligence, machine learning, cloud computing, AWS, Azure, digital transformation, custom software development, mobile app development, web development, business automation, IT consulting, software consulting, enterprise solutions, scalable technology, microservices, DevOps, agile development, tech innovation, startup technology, SaaS solutions, API development, data analytics, cybersecurity, technology partner";

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        keywords={keywords}
        image={`${baseUrl}/images/og-homepage.jpg`}
        url={baseUrl}
        type="website"
        section="Homepage"
        structuredData={getHomepageSchema()}
        robotsIndex={true}
        robotsFollow={true}
      />

      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <div
          className="h-1 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Hero Section */}
        <section className="relative min-h-screen bg-gradient-to-br from-[#0b1220] via-[#1a2332] to-[#0b1220] flex items-center justify-center overflow-hidden">
          {/* Enhanced Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Animated Gradient Orbs */}
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-[#E5C558]/20 to-yellow-400/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-3/4 right-1/4 w-80 h-80 bg-gradient-to-r from-blue-500/15 to-cyan-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
            <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-gradient-to-r from-purple-500/10 to-pink-400/10 rounded-full blur-3xl animate-pulse delay-2000"></div>

            {/* Floating Elements */}
            <div className="absolute top-20 right-20 w-4 h-4 bg-[#E5C558] rounded-full animate-ping opacity-75"></div>
            <div className="absolute top-1/2 left-20 w-2 h-2 bg-blue-400 rounded-full animate-ping opacity-50 delay-1000"></div>
            <div className="absolute bottom-32 right-1/3 w-3 h-3 bg-purple-400 rounded-full animate-ping opacity-60 delay-2000"></div>
          </div>

          {/* Grid Pattern Overlay */}
          <div className="absolute inset-0 opacity-5">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <defs>
                <pattern
                  id="hero-grid"
                  width="10"
                  height="10"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 10 0 L 0 0 0 10"
                    fill="none"
                    stroke="#E5C558"
                    strokeWidth="0.5"
                  />
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#hero-grid)" />
            </svg>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            {/* Hero Badge */}
            <div className="inline-flex items-center px-6 py-3 bg-[#E5C558]/20 backdrop-blur-sm rounded-full text-[#E5C558] font-semibold mb-8 border border-[#E5C558]/30 animate-fadeInUp">
              <FontAwesomeIcon icon={faRocket} className="mr-2" />
              Fresh. Focused. For You.
            </div>

            {/* Main Heading */}
            <div className="mb-12 animate-fadeInUp">
              <h1 className="text-2xl sm:text-4xl md:text-6xl lg:text-8xl font-bold mb-6 leading-tight tracking-tight">
                <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent">
                  Let's Build Your
                </span>
                <br />
                <span className="bg-gradient-to-r from-[#E5C558] via-[#f5d56d] to-[#E5C558] bg-clip-text text-transparent">
                  Success Story
                </span>
              </h1>
              <p className="text-base sm:text-lg md:text-2xl lg:text-3xl text-gray-300 max-w-5xl mx-auto mb-8 leading-relaxed font-light">
                We're a passionate startup building innovative solutions with
                <span className="text-[#E5C558] font-semibold">
                  {" "}
                  personal attention
                </span>{" "}
                and cutting-edge technology. Your growth is our mission.
              </p>
            </div>

            {/* Enhanced CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-20">
              <Link
                to="/contact-us"
                onClick={() => trackEvent("CTA", "Hero_GetStarted", "Homepage")}
                className="group inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220] font-bold text-lg rounded-full hover:shadow-2xl hover:shadow-[#E5C558]/50 transition-all duration-300 hover:scale-105 focus:ring-4 focus:ring-[#E5C558]/30"
              >
                Let's Talk - Free Consultation
                <FontAwesomeIcon
                  icon={faArrowRight}
                  className="ml-3 group-hover:translate-x-1 transition-transform"
                />
              </Link>
              <Link
                to="/services"
                onClick={() =>
                  trackEvent("CTA", "Hero_ExploreServices", "Homepage")
                }
                className="group inline-flex items-center justify-center px-10 py-5 bg-transparent border-2 border-[#E5C558] text-[#E5C558] font-bold text-lg rounded-full hover:bg-[#E5C558] hover:text-[#0b1220] transition-all duration-300 focus:ring-4 focus:ring-[#E5C558]/30"
              >
                <FontAwesomeIcon
                  icon={faPlay}
                  className="mr-3 group-hover:scale-110 transition-transform"
                />
                See What We Can Do
              </Link>
            </div>

            {/* Why Choose Us - Simplified for Startup */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
              <div className="group relative bg-white/10 backdrop-blur-sm rounded-3xl p-6 lg:p-8 hover:bg-white/20 transition-all duration-500 border border-white/20">
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-[#E5C558]/20 to-yellow-400/10 rounded-bl-3xl"></div>
                <div className="relative z-10 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#E5C558] to-[#f5d56d] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <FontAwesomeIcon
                      icon={faUsers}
                      className="text-white text-2xl"
                    />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 group-hover:text-[#E5C558] transition-colors">
                    Personal Touch
                  </h3>
                  <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                    Direct access to our team. No bureaucracy, just real people
                    solving real problems together.
                  </p>
                </div>
              </div>

              <div className="group relative bg-white/10 backdrop-blur-sm rounded-3xl p-6 lg:p-8 hover:bg-white/20 transition-all duration-500 border border-white/20">
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-blue-500/20 to-cyan-400/10 rounded-bl-3xl"></div>
                <div className="relative z-10 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <FontAwesomeIcon
                      icon={faShieldAlt}
                      className="text-white text-2xl"
                    />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                    Quality First
                  </h3>
                  <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                    Modern tech stack, best practices, and rigorous testing.
                    Startup agility with enterprise quality.
                  </p>
                </div>
              </div>

              <div className="group relative bg-white/10 backdrop-blur-sm rounded-3xl p-6 lg:p-8 hover:bg-white/20 transition-all duration-500 border border-white/20">
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-green-500/20 to-emerald-400/10 rounded-bl-3xl"></div>
                <div className="relative z-10 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-400 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <FontAwesomeIcon
                      icon={faTrophy}
                      className="text-white text-2xl"
                    />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 group-hover:text-green-400 transition-colors">
                    Your Success = Our Success
                  </h3>
                  <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                    Flexible pricing, milestone-based delivery. We succeed when
                    you succeed.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Page Sections - Optimized Flow */}
        <div className="mt-0 md:mt-24">
          <AboutUs />
          <ServicesSection services={services} />
          <WhyChooseUsSection />
          <OperationModelSection />
          <CEOQuotes quotes={quotes} />
          <ContactUs />
        </div>
      </div>
    </>
  );
};

export default Homepage;
